import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Drawer, Tag, theme, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { operationsApi, reportsApi, shipmentsApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { SkladDayDetail, StoreDayDetail } from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { OperationEditModal } from '../../components/OperationEditModal';
import { ActorLine } from '../../components/ActorLine';
import { PaymentMethodTag } from '../../components/PaymentMethodTag';
import { ShipmentItemsTable } from '../../components/ShipmentItemsTable';
import { useDownloadShipmentPdf } from '../../hooks/useDownloadShipmentPdf';
import { formatKopeks } from '../../lib/money';

const ROW_BORDER = '1px solid rgba(0,0,0,0.06)';

/** One tappable operation line (no products). */
function DetailRow({
  amount,
  tags,
  comment,
  actors,
  onClick,
}: {
  amount: number;
  tags?: ReactNode;
  comment?: string | null;
  /** Who entered / approved the row (rendered under the comment). */
  actors?: ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 8,
        padding: '10px 4px',
        borderTop: ROW_BORDER,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{tags}</div>
        {comment ? (
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }} ellipsis>
            {comment}
          </Typography.Text>
        ) : null}
        {actors}
      </div>
      <Typography.Text strong style={{ marginInlineStart: 8, whiteSpace: 'nowrap' }}>
        {formatKopeks(amount)}
      </Typography.Text>
    </div>
  );
}

/**
 * Lazily loads a накладная and shows its product lines (fetched only on expand).
 * A single накладная can span two sklads — the sklad view then shows one
 * purchase (and the store view one issue) PER sklad, each pointing at the same
 * shipmentId. `skladId` scopes the lines to just this leg's sklad so each card
 * shows its own goods instead of the whole document duplicated. */
function ShipmentItemsPanel({
  shipmentId,
  skladId,
  priceMode,
}: {
  shipmentId: string;
  skladId: string | null;
  priceMode: 'buy' | 'sell';
}): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.shipment(shipmentId),
    queryFn: () => shipmentsApi.get(shipmentId),
  });
  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;
  const items =
    skladId == null
      ? query.data.items
      : query.data.items.filter((item) => item.legSkladId === skladId);
  return <ShipmentItemsTable items={items} priceMode={priceMode} />;
}

/**
 * A накладная-backed issue/purchase row, shown as its own bordered card with
 * the products ALREADY expanded — no click needed, easy to tell накладные
 * apart. Header = tags + amount; body = product lines + a PDF download and a
 * link to the full накладная. The amount is document-owned, so there is no raw
 * per-operation edit here. */
function ShipmentCard({
  amount,
  comment,
  tags,
  shipmentId,
  shipmentName,
  skladId,
  priceMode,
  actors,
}: {
  amount: number;
  comment: string | null;
  tags: ReactNode;
  shipmentId: string;
  shipmentName: string | null;
  /** Scopes the shown lines to this leg's sklad (multi-sklad накладные). */
  skladId: string | null;
  priceMode: 'buy' | 'sell';
  /** Who posted the накладная. */
  actors?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { download: downloadPdf, isPending: pdfPending } = useDownloadShipmentPdf();
  // sklad card → full owner copy scoped to this sklad's leg; store card → the
  // whole накладная with issue prices only (the store never sees purchase cost).
  const pdfView = priceMode === 'buy' ? 'sklad' : 'store';

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: 10,
        marginBlock: 8,
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {tags}
        </div>
        <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
          {formatKopeks(amount)}
        </Typography.Text>
      </div>
      <ShipmentItemsPanel shipmentId={shipmentId} skladId={skladId} priceMode={priceMode} />
      {comment ? (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {comment}
        </Typography.Text>
      ) : null}
      {actors}
      {/* No raw «Изменить операцию» here: a накладная-backed amount is owned by
          the document. Its total is changed by re-pricing the накладная, so the
          only action is to open it (unpost → change prices → repost). */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={pdfPending}
          onClick={() =>
            void downloadPdf({
              id: shipmentId,
              view: pdfView,
              msName: shipmentName ?? shipmentId,
              skladId: pdfView === 'sklad' ? (skladId ?? undefined) : undefined,
            })
          }
        >
          {t('shipments.downloadPdf')}
        </Button>
        <Button size="small" type="link" onClick={() => navigate(`/shipments/${shipmentId}`)}>
          {t('daygrid.detail.openShipment')}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {title}
      </Typography.Text>
      {children}
    </div>
  );
}

function StoreDetailBody({
  data,
  onEdit,
}: {
  data: StoreDayDetail;
  onEdit: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (data.issues.length + data.payments.length + data.salary.length + data.expenses.length === 0) {
    return <EmptyBlock description={t('daygrid.detail.empty')} />;
  }
  return (
    <>
      {data.issues.length > 0 ? (
        <Section title={t('daygrid.detail.issues')}>
          {data.issues.map((it) => {
            const tags = (
              <>
                {it.skladName ? <Tag color="blue">{it.skladName}</Tag> : <Tag>{t('daygrid.other')}</Tag>}
                {it.shipmentName ? (
                  <Tag color="purple">{t('daygrid.detail.shipmentTag', { name: it.shipmentName })}</Tag>
                ) : null}
              </>
            );
            return it.shipmentId ? (
              <ShipmentCard
                key={it.operationId}
                amount={it.amount}
                comment={it.comment}
                tags={tags}
                shipmentId={it.shipmentId}
                shipmentName={it.shipmentName}
                skladId={it.skladId}
                priceMode="sell"
                actors={<ActorLine postedBy={it.postedByName} />}
              />
            ) : (
              <DetailRow
                key={it.operationId}
                amount={it.amount}
                comment={it.comment}
                tags={tags}
                actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
                onClick={() => onEdit(it.operationId)}
              />
            );
          })}
        </Section>
      ) : null}
      {data.payments.length > 0 ? (
        <Section title={t('daygrid.detail.payments')}>
          {data.payments.map((it) => (
            <DetailRow
              key={it.operationId}
              amount={it.amount}
              comment={it.comment}
              tags={<PaymentMethodTag method={it.paymentMethod} />}
              actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
              onClick={() => onEdit(it.operationId)}
            />
          ))}
        </Section>
      ) : null}
      {data.salary.length > 0 ? (
        <Section title={t('daygrid.colSalary')}>
          {data.salary.map((it) => (
            <DetailRow
              key={it.operationId}
              amount={it.amount}
              comment={it.comment}
              tags={<Tag color="gold">{t('daygrid.colSalary')}</Tag>}
              actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
              onClick={() => onEdit(it.operationId)}
            />
          ))}
        </Section>
      ) : null}
      {data.expenses.length > 0 ? (
        <Section title={t('daygrid.colExpenses')}>
          {data.expenses.map((it) => (
            <DetailRow
              key={it.operationId}
              amount={it.amount}
              comment={it.comment}
              tags={it.categoryName ? <Tag>{it.categoryName}</Tag> : null}
              actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
              onClick={() => onEdit(it.operationId)}
            />
          ))}
        </Section>
      ) : null}
    </>
  );
}

function SkladDetailBody({
  data,
  skladId,
  onEdit,
}: {
  data: SkladDayDetail;
  /** The drawer's own sklad — scopes each накладная card to this sklad's lines. */
  skladId: string;
  onEdit: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (data.purchases.length + data.payments.length === 0) {
    return <EmptyBlock description={t('daygrid.detail.empty')} />;
  }
  return (
    <>
      {data.purchases.length > 0 ? (
        <Section title={t('daygrid.detail.purchases')}>
          {data.purchases.map((it) => {
            const tags = (
              <>
                {it.storeName ? <Tag color="blue">{it.storeName}</Tag> : <Tag>{t('daygrid.other')}</Tag>}
                {it.shipmentName ? (
                  <Tag color="purple">{t('daygrid.detail.shipmentTag', { name: it.shipmentName })}</Tag>
                ) : null}
              </>
            );
            return it.shipmentId ? (
              <ShipmentCard
                key={it.operationId}
                amount={it.amount}
                comment={it.comment}
                tags={tags}
                shipmentId={it.shipmentId}
                shipmentName={it.shipmentName}
                skladId={skladId}
                priceMode="buy"
                actors={<ActorLine postedBy={it.postedByName} />}
              />
            ) : (
              <DetailRow
                key={it.operationId}
                amount={it.amount}
                comment={it.comment}
                tags={tags}
                actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
                onClick={() => onEdit(it.operationId)}
              />
            );
          })}
        </Section>
      ) : null}
      {data.payments.length > 0 ? (
        <Section title={t('daygrid.detail.payments')}>
          {data.payments.map((it) => (
            <DetailRow
              key={it.operationId}
              amount={it.amount}
              comment={it.comment}
              actors={<ActorLine enteredBy={it.enteredByName} approvedBy={it.approvedByName} />}
              onClick={() => onEdit(it.operationId)}
            />
          ))}
        </Section>
      ) : null}
    </>
  );
}

/**
 * Loads one operation by id (day-detail rows only carry the id) and opens the
 * shared edit modal. Editing/deleting invalidates the money roots, so the grid
 * and this drawer refetch. */
function EditOperationLoader({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: ['operation', id],
    queryFn: () => operationsApi.get(id!),
    enabled: id !== null,
  });
  return <OperationEditModal operation={query.data ?? null} onClose={onClose} />;
}

/**
 * Drill-down for a clicked day cell (SPEC — «Jadval» view): what the day's
 * numbers are made of. Store: issues (with source sklad + накладная), payments,
 * salary, other expenses. Sklad: purchases (with destination store + накладная)
 * and payments. Накладная rows expand to show their products; other rows tap to
 * edit.
 */
export function DayDetailDrawer({
  kind,
  id,
  date,
  onClose,
}: {
  kind: 'store' | 'sklad';
  id: string;
  date: string | null;
  onClose: () => void;
}): JSX.Element {
  const [editingId, setEditingId] = useState<string | null>(null);

  const storeQuery = useQuery({
    queryKey: queryKeys.dayDetail('store', id, date ?? ''),
    queryFn: () => reportsApi.storeDay(id, date!),
    enabled: date !== null && kind === 'store',
  });
  const skladQuery = useQuery({
    queryKey: queryKeys.dayDetail('sklad', id, date ?? ''),
    queryFn: () => reportsApi.skladDay(id, date!),
    enabled: date !== null && kind === 'sklad',
  });
  const query = kind === 'store' ? storeQuery : skladQuery;

  return (
    <Drawer
      open={date !== null}
      onClose={onClose}
      title={date ? dayjs(date).format('D MMMM YYYY') : ''}
      placement="bottom"
      height="80%"
      styles={{ body: { display: 'grid', gap: 16, alignContent: 'start' } }}
    >
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : kind === 'store' && storeQuery.data ? (
        <StoreDetailBody data={storeQuery.data} onEdit={setEditingId} />
      ) : kind === 'sklad' && skladQuery.data ? (
        <SkladDetailBody data={skladQuery.data} skladId={id} onEdit={setEditingId} />
      ) : null}

      <EditOperationLoader id={editingId} onClose={() => setEditingId(null)} />
    </Drawer>
  );
}
